import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router";
import { MARKET_CONDITIONS, OFFER_PARAM, PAYMENT_LABEL, type ProductOffer, type ProductOffers as Offers } from "../../shared/market";
import { CONDITION_LABEL, FINISH_LABEL, LANGUAGE_LABEL, type Condition } from "../../shared/variants";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { marketApi } from "./api";
import { useMarketCart } from "./cart";
import { AddToCart, Avatar, ReportButton, SellerPhotos, Stars, useArs } from "./components";
import { OfferDialog } from "./OfferDialog";
import { useMarketViewer } from "./viewer";

/** Card conditions from best to worst: a minimum keeps that one and the better ones. */
const RANK: Record<string, number> = Object.fromEntries(MARKET_CONDITIONS.map((c, i) => [c, i]));

interface Filters {
  minCondition: string;
  language: string;
  caba: boolean;
  cash: boolean;
}
const NONE: Filters = { minCondition: "", language: "", caba: false, cash: false };

function matches(o: ProductOffer, f: Filters): boolean {
  if (f.minCondition && o.condition !== "SEALED" && RANK[o.condition] > RANK[f.minCondition]) return false;
  if (f.language && o.language !== f.language) return false;
  // In CABA: hand delivery there, or the seller ships.
  if (f.caba && !o.delivery.handZones.includes("caba") && !o.delivery.ship) return false;
  if (f.cash && !o.payments.includes("cash")) return false;
  return true;
}

/**
 * Below the store's own offer on a product page: every community offer, cheapest first, like a
 * marketplace's offers table (a real table on desktop, stacked cards on phones), with filters. Each
 * offer is bought right there (a listing has no page of its own): "Comprar", the cart, and under "···"
 * a price offer, ♡ and a report. `highlight`: the offer a link points at (?oferta=), marked and
 * scrolled to; `gone`: that link's listing isn't for sale any more.
 */
export function ProductOffers({
  productId,
  data,
  sealed,
  highlight = null,
  gone = false,
}: {
  productId: number;
  /** Undefined while loading or after an error: nothing shows (not "nobody sells it"). */
  data: Offers | undefined;
  sealed: boolean;
  highlight?: number | null;
  /** "mine": it's the viewer's own, paused or sold out (they turn it on in Mi tienda). */
  gone?: boolean | "mine";
}) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const auth = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>(NONE);
  const [wanted, setWanted] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const items = data?.items ?? [];
  const languages = useMemo(() => [...new Set(items.map((o) => o.language))], [items]);
  // The offer a link points at stays in the table whatever the filters.
  const shown = items.filter((o) => o.id === highlight || matches(o, filters));
  const filtering = JSON.stringify(filters) !== JSON.stringify(NONE);
  const found = highlight !== null && items.some((o) => o.id === highlight);
  // Arriving from a link: to that offer once the table is there (or to the table, when it's gone).
  useEffect(() => {
    if (!data || highlight === null) return;
    if (found) document.getElementById(`oferta-${highlight}`)?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    else if (gone) document.getElementById("ofertas")?.scrollIntoView?.({ behavior: "smooth" });
  }, [data, highlight, found, gone]);
  if (!data) return null;

  const want = async () => {
    if (!auth.user) return navigate(`/ingresar?next=${encodeURIComponent(window.location.pathname)}`);
    setWanted("saving");
    try {
      await marketApi.addWant(productId);
      setWanted("saved");
    } catch {
      setWanted("error");
    }
  };

  const wantButton =
    wanted === "saved" ? (
      <span role="status">
        ✓ {t("mkt.wantSaved")} <span className="muted small">{t("mkt.wantsPrivacy")}</span>
      </span>
    ) : (
      <button type="button" className="btn btn-outline btn-sm" disabled={wanted === "saving"} onClick={want} title={t("mkt.wantsPrivacy")}>
        🔎 {t("mkt.wantThis")}
      </button>
    );

  return (
    <section className="section offers" id="ofertas" aria-labelledby="offers-title">
      <div className="section-head">
        <h2 id="offers-title" className="section-title">
          {t("offers.title")} {data.count > 0 && <span className="offers-count">{t("offers.count", { count: data.count, price: ars(data.minPriceArs ?? 0) })}</span>}
        </h2>
      </div>
      {gone && !found && (
        <p className="notice" role="status">
          {gone === "mine" ? (
            <>
              {t("offers.goneMine")} <Link to="/mi-tienda/publicaciones">{t("mkt.editInShop")}</Link>
            </>
          ) : (
            t("offers.gone")
          )}
        </p>
      )}

      {data.count === 0 ? (
        <div className="offers-empty">
          <p className="muted">{t("offers.none")}</p>
          <div className="btn-row">
            {wantButton}
            <Link to="/vender" className="btn btn-link btn-sm">
              {t("offers.sellYours")}
            </Link>
          </div>
          {wanted === "error" && <p className="form-error">{t("error.generic")}</p>}
        </div>
      ) : (
        <>
          <div className="offers-filters" role="group" aria-label={t("offers.filters")}>
            {!sealed && (
              <label>
                {t("offers.minCondition")}{" "}
                <select value={filters.minCondition} onChange={(e) => setFilters({ ...filters, minCondition: e.target.value })}>
                  <option value="">{t("offers.any")}</option>
                  {MARKET_CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {CONDITION_LABEL[c][locale]}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {languages.length > 1 && (
              <label>
                {t("offers.language")}{" "}
                <select value={filters.language} onChange={(e) => setFilters({ ...filters, language: e.target.value })}>
                  <option value="">{t("offers.any")}</option>
                  {languages.map((l) => (
                    <option key={l} value={l}>
                      {LANGUAGE_LABEL[l]?.[locale] ?? l}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="check">
              <input type="checkbox" checked={filters.caba} onChange={(e) => setFilters({ ...filters, caba: e.target.checked })} /> {t("offers.caba")}
            </label>
            <label className="check">
              <input type="checkbox" checked={filters.cash} onChange={(e) => setFilters({ ...filters, cash: e.target.checked })} /> {t("offers.cash")}
            </label>
            {filtering && (
              <button type="button" className="btn btn-link btn-sm" onClick={() => setFilters(NONE)}>
                {t("offers.clear")}
              </button>
            )}
          </div>

          {shown.length === 0 ? (
            <p className="muted">{t("offers.noMatch")}</p>
          ) : (
            <table className="offers-table">
              <thead>
                <tr>
                  <th scope="col">{t("offers.seller")}</th>
                  <th scope="col">{t("offers.item")}</th>
                  <th scope="col">{t("offers.delivery")}</th>
                  <th scope="col" className="num">
                    {t("offers.price")}
                  </th>
                  <th scope="col">
                    <span className="sr-only">{t("offers.action")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((o) => (
                  <OfferRow key={o.id} offer={o} highlighted={o.id === highlight} />
                ))}
              </tbody>
            </table>
          )}
          <div className="btn-row offers-foot">
            {data.count > items.length && (
              <Link to={`/mercado?producto=${productId}&orden=price_asc`} className="btn btn-link btn-sm">
                {t("offers.seeAll", { count: data.count })}
              </Link>
            )}
            {wantButton}
            <Link to="/vender" className="btn btn-link btn-sm">
              {t("offers.sellYours")}
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

function OfferRow({ offer: o, highlighted }: { offer: ProductOffer; highlighted: boolean }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const s = o.seller;
  const auth = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const cart = useMarketCart();
  const viewer = useMarketViewer();
  const [offering, setOffering] = useState(false);
  const menu = useRef<HTMLDetailsElement>(null);
  const closeMenu = () => menu.current?.removeAttribute("open");
  const mine = viewer.isMine(s.handle);
  const saved = viewer.isFavorite(o.id);
  // Back here, at this offer, after signing in.
  const here = () => {
    const params = new URLSearchParams(search);
    params.set(OFFER_PARAM, String(o.id));
    return `${pathname}?${params}`;
  };
  const needLogin = () => navigate(`/ingresar?next=${encodeURIComponent(here())}`);
  const buy = () => {
    if (!auth.user) return needLogin();
    cart.add(s.handle, { listingId: o.id, quantity: 1, max: o.quantity, name: o.name, priceArs: o.priceArs, photo: o.catalogImage });
    navigate(`/mercado/comprar/${s.handle}`);
  };
  const delivery = [
    o.delivery.handZones.length > 0 && `🤝 ${o.delivery.handZones.includes("caba") ? (o.delivery.handZones.length > 1 ? "CABA y GBA" : "CABA") : "GBA"}${o.delivery.handCostArs ? ` ${ars(o.delivery.handCostArs)}` : ""}`,
    o.delivery.ship && `📦 ${o.delivery.shipCostArs === null ? t("offers.shipQuote") : o.delivery.shipCostArs === 0 ? t("offers.free") : ars(o.delivery.shipCostArs)}`,
    o.delivery.pickup && `📍 ${t("offers.pickup")}`,
  ].filter(Boolean) as string[];
  return (
    <tr id={`oferta-${o.id}`} className={highlighted ? "offer-highlight" : undefined} aria-current={highlighted ? "true" : undefined}>
      <td data-label={t("offers.seller")}>
        <Link to={`/vendedores/${s.handle}`} className="offer-seller">
          <Avatar url={s.avatarUrl} name={s.displayName} size={28} />
          <span>
            <strong>{s.displayName}</strong>
            <span className="muted small">
              <Stars value={s.ratingAvg} count={s.ratingCount} size="sm" /> · {t("mkt.salesCount", { count: s.sales })}
              {s.city ? ` · ${s.city}` : ""}
              {s.verified && <span className="seller-verified"> · 🪪 {t("mkt.verified")}</span>}
              {s.isNew && <span className="seller-new"> · 🆕 {t("mkt.newSeller")}</span>}
            </span>
          </span>
        </Link>
      </td>
      <td data-label={t("offers.item")}>
        <span className="offer-chips">
          <span className={`chip chip-cond chip-${o.condition.toLowerCase()}`} title={CONDITION_LABEL[o.condition as Condition]?.[locale]}>
            {o.condition === "SEALED" ? CONDITION_LABEL.SEALED[locale] : o.condition}
          </span>
          <span className="chip">{LANGUAGE_LABEL[o.language]?.[locale] ?? o.language}</span>
          {o.finish !== "normal" && <span className="chip">{FINISH_LABEL[o.finish][locale]}</span>}
          {o.grader && <span className="chip chip-graded">{`${o.grader} ${o.grade}`}</span>}
          <SellerPhotos listing={o} thumb />
        </span>
        {o.notes && <OfferNotes notes={o.notes} />}
      </td>
      <td data-label={t("offers.delivery")}>
        <span className="small">{delivery.join(" · ") || "—"}</span>
        <span className="muted small offer-pay">{o.payments.map((p) => PAYMENT_LABEL[p][locale]).join(" · ")}</span>
      </td>
      <td data-label={t("offers.price")} className="num">
        <strong className="offer-price">{ars(o.priceArs)}</strong>
        {o.quantity > 1 && <span className="muted small">{t("mkt.available", { count: o.quantity })}</span>}
      </td>
      <td>
        {mine ? (
          <span className="offer-actions">
            <span className="tag">{t("offers.yours")}</span>
            <Link to={`/mi-tienda/publicaciones?q=${encodeURIComponent(o.name)}`} className="btn btn-link btn-sm">
              {t("mkt.editInShop")}
            </Link>
          </span>
        ) : (
          <span className="offer-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={buy}>
              {t("offers.buy")}
            </button>
            <AddToCart listing={o} />
            <details className="offer-more" ref={menu}>
              <summary aria-label={t("offers.more")} title={t("offers.more")}>
                ···
              </summary>
              <div className="offer-more-panel">
                {o.acceptsOffers && (
                  <button type="button" className="btn btn-link btn-sm" onClick={() => (closeMenu(), auth.user ? setOffering(true) : needLogin())}>
                    {t("mkt.makeOffer")}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  aria-pressed={saved}
                  onClick={() => (auth.user ? void viewer.setFavorite(o.id, !saved).catch(() => undefined) : needLogin())}
                >
                  {saved ? `♥ ${t("mkt.saved")}` : `♡ ${t("mkt.save")}`}
                </button>
                <span onClickCapture={closeMenu}>
                  <ReportButton target="listing" targetId={String(o.id)} />
                </span>
              </div>
            </details>
          </span>
        )}
        {offering && createPortal(<OfferDialog listingId={o.id} max={o.quantity} listPrice={o.priceArs} onClose={() => setOffering(false)} />, document.body)}
      </td>
    </tr>
  );
}

/** The seller's notes on a copy: a line or two, the rest on asking (there's no listing page to read them on). */
function OfferNotes({ notes }: { notes: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const long = notes.length > NOTES_SHORT;
  return (
    <span className={`offer-notes muted small${open ? " is-open" : ""}`}>
      {open || !long ? notes : `${notes.slice(0, NOTES_SHORT).trimEnd()}…`}{" "}
      {long && (
        <button type="button" className="btn btn-link btn-sm offer-notes-more" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? t("offers.notesLess") : t("offers.notesMore")}
        </button>
      )}
    </span>
  );
}
/** Characters of the notes shown before "Ver más". */
const NOTES_SHORT = 90;
